import { useEffect, useRef, useState } from 'react';
import { formatDate, formatDateTime } from '../../../lib/format';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type {
  BillingTotals, CompanyDetail as CompanyShape, Industry, InvoiceRow, OnboardingStatus,
  RateCardRow, TenantStatus,
} from '@dru/types';
import {
  ONBOARDING_LABEL, SETUP_MODE_LABEL, TENANT_STATUS_LABEL, WORKFORCE_BANDS,
} from '@dru/types';
import {
  AddressBlock, addressForm, addressPayload, Alert, Button, Checklist, ContactBlock, DetailHeader,
  DetailSection, ErrorState, Facts, InfoTip, Input, cn, SelectField, StatusBadge, Tabs, TextField,
  TextSkeleton, useIdempotencyKey, type AddressFormValue, type CheckItem, type Figure, type Tone,
} from '@dru/ui';
import { ApiError } from '@dru/api-client';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useBreadcrumbTitle } from '../../../layouts/BreadcrumbContext';
import { holds } from '../../../layouts/ConsoleShell';
import { useAuth, errorMessage } from '../../../lib/auth';
import { Dialog } from '../../../components/Dialog';
import { AddressFields } from '../../../components/AddressFields';
import { IndustryCombobox, industryFromRef } from '../../../components/IndustryCombobox';
import { CompanySetup, SETUP_SECTION_TAB, type SetupSection } from '../components/CompanySetup';
import { CompanyBilling, money } from '../components/CompanyBilling';
import { CompanyPackages } from '../components/CompanyPackages';
import { CompanyActivity } from '../components/CompanyActivity';
import { EmergencyArrangements } from '../components/EmergencyArrangements';
import { CompanyLabAssignments } from '../components/CompanyLabAssignments';
import { OVERSIGHT_TABS, Panel, type Tab as OversightTab } from './Oversight';
import { LeadOrigin } from '../../ops/components/LeadOrigin';
import { AgreementsSection, CompanyExtrasSection } from '../components/CompanyProfileExtras';
import { OnboardingGuide } from '../components/OnboardingGuide';

/**
 * One company, in full.
 *
 * Read the way a customer account is read: who they are and what state the
 * account is in, the few numbers that describe it (workers, plants, logins,
 * balance), what needs attention, and only then the sections. Registration
 * metadata — paperwork, legal name, GSTIN, ID prefix — is on the page but
 * below the contact and address, because a person opening a customer record
 * is almost never asking about it.
 *
 * Every act on the page is offered only to a role the API would let do it:
 * Support reads the registry and nothing else, Operations sets companies up
 * but does not bill them, Finance bills them but does not edit them. A button
 * that always answers 403 teaches people that errors are normal.
 *
 * The company's shape — plants and who can sign in — comes from the setup
 * read (`tenant.setup`), which is structure and never a clinical record. That
 * is what lets the figures say "4 plants" instead of sending an operator to
 * justify a privileged read to learn a number the Setup tab already shows.
 *
 * Company records open directly on a permission-filtered tab. Each resource
 * read is audited by the backend without asking the administrator for a reason.
 */

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'plants', label: 'Plants' },
  { key: 'workforce', label: 'Workforce' },
  { key: 'examinations', label: 'Examinations' },
  { key: 'certificates', label: 'Certificates' },
  { key: 'users', label: 'Users' },
  // Key kept so old links land here; the per-company configuration it
  // used to show gave way to the two default packages (29 Oct 2026).
  { key: 'medical', label: 'Packages' },
  { key: 'requests', label: 'Change requests' },
  { key: 'billing', label: 'Billing' },
  // Which venues treat this company's employees in an emergency (owner, 4 Nov 2026).
  { key: 'partners', label: 'Labs & emergency' },
  { key: 'activity', label: 'Activity' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/** Links written against the old tab names still land on the right section. */
const LEGACY_TAB: Record<string, TabKey> = {
  people: 'users', 'hr-users': 'users', organisation: 'plants', employees: 'workforce', emergency: 'partners',
};

/** The company's records, each its own tab, with the read permission it needs. */
const RECORD_TAB: Partial<Record<TabKey, { panel: OversightTab; need: string }>> = Object.fromEntries(
  OVERSIGHT_TABS.map((t) => [t.key === 'employees' ? 'workforce' : t.key, { panel: t.key, need: t.need }]),
);

/** The parts of setup that are their own tabs, and the setup view each opens. */
const SETUP_VIEW: Partial<Record<TabKey, SetupSection>> = {
  plants: 'structure', users: 'users', requests: 'requests',
};

/** What this reader may do here, mirroring the guards on each endpoint. */
function abilities(permissions: ReadonlySet<string>) {
  return {
    edit: holds(permissions, 'tenant.update'),
    lifecycle: holds(permissions, 'tenant.activate'),
    setup: holds(permissions, ['tenant.setup', 'tenant.structure-manage']),
    // `GET /invoices?tenantId=` — reading another company's invoices from the
    // platform — is guarded by invoice.create, not invoice.read.
    billing: holds(permissions, 'invoice.create'),
    audit: holds(permissions, 'audit.read'),
    oversight: holds(permissions, OVERSIGHT_TABS.map((t) => t.need)),
    impersonate: holds(permissions, 'platform.impersonate'),
  };
}
type Can = ReturnType<typeof abilities>;

export function CompanyDetail() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { permissions } = useAuth();
  const can = abilities(permissions);

  const company = useQuery<CompanyShape>(() => api.get(`/tenants/${id}`), [id]);
  const c = company.data;
  const live = c?.status === 'ACTIVE' || c?.status === 'SUSPENDED';

  // The balance is the one billing fact that belongs beside the name. The
  // API sums its totals over the page it returns, not the account, so this
  // asks for the same page the Billing tab reads: a one-row page reported
  // "nothing owed" for a company with three unpaid invoices.
  const wantBilling = can.billing && Boolean(c) && c?.status !== 'PENDING';
  const billing = useQuery<{ items: InvoiceRow[]; totals: BillingTotals } | null>(
    () => (wantBilling ? api.get(`/invoices?tenantId=${id}&limit=100`) : Promise.resolve(null)),
    [id, wantBilling],
  );
  // Setup is computed from what exists, so it is the truthful figure for
  // plants, logins and the workforce on the roll — and for how far setup is.
  const wantSetup = can.setup && live;
  const setup = useQuery<OnboardingStatus | null>(
    () => (wantSetup ? api.get(`/admin/tenants/${id}/setup`) : Promise.resolve(null)),
    [id, wantSetup],
  );
  // Whether anything can be invoiced: setup has no rate-card step, and an
  // invoice run against a company without one fails with "No published rate
  // card covers…" (flow audit 2.9). Read with the billing figures, by whoever
  // may read them, and surfaced below as a line to act on — never a blocker.
  const rates = useQuery<{ items: RateCardRow[] } | null>(
    () => (wantBilling ? api.get(`/rate-cards?tenantId=${id}`) : Promise.resolve(null)),
    [id, wantBilling],
  );
  const [editing, setEditing] = useState(false);
  // The guide's "Review and approve" opens the same dialog as the header's Approve.
  const [approveAsk, setApproveAsk] = useState(0);

  useBreadcrumbTitle(c?.name);

  const visibleTab = (key: TabKey): boolean => {
    // One Plants tab: managed through setup where the reader may, otherwise
    // the read-only record.
    if (key === 'plants') return (live && can.setup) || holds(permissions, RECORD_TAB.plants!.need);
    if (RECORD_TAB[key]) return holds(permissions, RECORD_TAB[key]!.need);
    // The rest of setup needs the full setup permission, not structure only.
    if (key === 'users' || key === 'medical' || key === 'requests') return live && holds(permissions, 'tenant.setup');
    if (key === 'billing') return can.billing && c?.status !== 'PENDING';
    if (key === 'activity') return can.audit;
    if (key === 'partners') return c?.status !== 'PENDING' && holds(permissions, ['partner.assignment-manage', 'emergency.arrangement-manage']);
    return true;
  };
  // Old links: `?tab=setup&section=users` for a part of setup, and
  // `?tab=records&records=examinations` for one kind of record.
  const legacySection = params.get('tab') === 'setup' ? params.get('section') : null;
  const legacyRecords = params.get('tab') === 'records'
    ? (params.get('records') ?? (['workforce', 'plants', 'examinations', 'certificates'] as const).find((k) => visibleTab(k)) ?? 'overview')
    : null;
  const raw = (legacySection && legacySection in SETUP_SECTION_TAB
    ? SETUP_SECTION_TAB[legacySection as SetupSection]
    : legacyRecords ?? params.get('tab')) ?? 'overview';
  const asked: TabKey = LEGACY_TAB[raw] ?? (TABS.some((t) => t.key === raw) ? raw as TabKey : 'overview');
  const tab: TabKey = !c || visibleTab(asked) ? asked : 'overview';

  const setTab = (key: string) => {
    const next = new URLSearchParams(params);
    next.set('tab', key);
    next.delete('section');
    setParams(next, { replace: true });
  };

  if (company.error) {
    return <ErrorState detail={company.error} onRetry={company.reload} />;
  }

  const s = setup.data;
  const outstanding = billing.data?.totals.outstandingPaise ?? null;

  return (
    <div className="space-y-5">
      <DetailHeader
        eyebrow="Company"
        title={c?.name ?? 'Loading…'}
        badges={c && (
          <>
            <StatusBadge tone={statusTone(c.status)}>{TENANT_STATUS_LABEL[c.status]}</StatusBadge>
          </>
        )}
        // Says what is recorded, and names only what is missing that matters:
        // the location. An industry not chosen is simply left out rather than
        // announced on every visit (flow audit 1.5).
        subtitle={c && [
          [c.city, c.state].filter(Boolean).join(', ') || 'Location not recorded',
          c.industryRef?.name ?? c.industry,
        ].filter(Boolean).join(' · ')}
        actions={c && (
          <>
            <AttentionMark items={needs(c, s, outstanding, billing.loading, can, { setTab, onEdit: () => setEditing(true) }, rates.data)} />
            {/* Not before approval: a registration has no plants or HR yet. */}
            {can.setup && c.status !== 'PENDING' && (
              <Link
                to={`/admin/companies/${id}/employees/import`}
                className="inline-flex h-10 items-center rounded-btn border border-neutral-300 bg-white px-4 text-body font-semibold text-neutral-800 hover:bg-neutral-50"
              >
                Import employees
              </Link>
            )}
            {can.edit && <Button variant="secondary" onClick={() => setEditing(true)}>Edit details</Button>}
            {can.lifecycle && <Lifecycle company={c} approveAsk={approveAsk} onChanged={() => { company.reload(); setup.reload(); }} />}
          </>
        )}
      />
      <LeadOrigin tenantId={id} />

      {company.loading && !c && <TextSkeleton lines={4} />}

      {c && (
        <>
          <FigureLine figures={figures(c, s, can, { outstanding, billingLoading: billing.loading, billingError: billing.error, setupLoading: setup.loading && wantSetup, onBilling: () => setTab('billing') })} />
        </>
      )}

      <Tabs
        tabs={TABS.map((t) => ({
          key: t.key,
          label: t.label,
          hint: t.key === 'requests' && s?.counts.openChangeRequests ? s.counts.openChangeRequests
            : undefined,
          visible: visibleTab(t.key),
        }))}
        active={tab}
        onChange={setTab}
      />

      {c && tab === 'overview' && (
        <OnboardingGuide
          company={c}
          setup={s ?? null}
          setupLoading={setup.loading}
          can={{ lifecycle: can.lifecycle, setup: can.setup, fullSetup: holds(permissions, 'tenant.setup'), edit: can.edit }}
          onApprove={() => setApproveAsk((n) => n + 1)}
          onEdit={() => setEditing(true)}
          setTab={setTab}
          onChanged={() => { setup.reload(); company.reload(); }}
        />
      )}
      {c && tab === 'overview' && <Overview company={c} can={can} onEdit={() => setEditing(true)} onChanged={company.reload} />}
      {c && RECORD_TAB[tab] && !(tab === 'plants' && SETUP_VIEW.plants && live && can.setup) && (
        <Panel key={`${c.id}:${tab}`} tenantId={c.id} tab={RECORD_TAB[tab]!.panel} />
      )}
      {c && SETUP_VIEW[tab] && live && can.setup && (
        // The company row follows as well as the figures: an address or an
        // industry added in Configuration shows in the header at once.
        <CompanySetup
          key={tab}
          view={SETUP_VIEW[tab]!}
          tenantId={c.id}
          industry={c.industryRef ?? null}
          onChanged={() => { setup.reload(); company.reload(); }}
        />
      )}
      {c && tab === 'medical' && live && holds(permissions, 'tenant.setup') && <CompanyPackages tenantId={c.id} />}
      {c && tab === 'billing' && <CompanyBilling tenantId={c.id} />}
      {c && tab === 'partners' && (
        <div className="space-y-8">
          {holds(permissions, 'partner.assignment-manage') && <CompanyLabAssignments tenantId={c.id} />}
          {holds(permissions, 'emergency.arrangement-manage') && <EmergencyArrangements tenantId={c.id} />}
        </div>
      )}
      {c && tab === 'activity' && <CompanyActivity tenantId={c.id} />}

      {c && editing && (
        <EditDialog
          company={c}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); company.reload(); billing.reload(); setup.reload(); }}
        />
      )}
    </div>
  );
}

/**
 * The account's few numbers on one line under the name:
 * "Employees 9 · Plants 1 · Logins 0 · Outstanding ₹0.00". The note that
 * explains a number ("stated at registration") is its tooltip.
 */
function FigureLine({ figures: list }: { figures: Array<Figure | false | null | undefined> }) {
  const shown = list.filter(Boolean) as Figure[];
  if (!shown.length) return null;
  return (
    <ul aria-label="At a glance" className="flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-card border border-neutral-200 bg-white px-4 py-2.5 text-small">
      {shown.map((f) => {
        const body = (
          <>
            <span className="text-neutral-500">{f.label}</span>
            {f.loading
              ? <span className="inline-block h-4 w-8 animate-pulse rounded bg-neutral-100 align-middle" />
              : <span className={cn('font-semibold tabular-nums', f.tone === 'overdue' ? 'text-overdue-600' : 'text-neutral-900')}>{f.value}</span>}
          </>
        );
        const tip = typeof f.hint === 'string' ? f.hint : undefined;
        return (
          <li key={f.key} title={tip} className="inline-flex items-baseline gap-1.5">
            {f.onClick
              ? <button type="button" onClick={f.onClick} className="inline-flex items-baseline gap-1.5 rounded hover:underline max-md:min-h-10 max-md:items-center">{body}</button>
              : body}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * What needs looking at, as a small mark beside the actions. The list opens
 * on click, so the page leads with the work rather than a checklist.
 */
function AttentionMark({ items }: { items: Array<CheckItem | false | null | undefined> }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const shown = items.filter(Boolean) as CheckItem[];
  const open_ = shown.filter((i) => i.state !== 'done');
  const urgent = open_.some((i) => i.state === 'blocked');

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        aria-expanded={open}
        title={open_.length ? `${open_.length} to check` : 'In good standing'}
        onClick={() => setOpen((v) => !v)}
        className={
          'inline-flex h-10 items-center gap-1.5 rounded-btn border px-3 text-small font-semibold ' + (
            open_.length === 0 ? 'border-neutral-300 bg-white text-covered-600'
              : urgent ? 'border-danger-600/40 bg-danger-100 text-danger-600'
                : 'border-warning-600/40 bg-warning-100 text-warning-600')
        }
      >
        <span aria-hidden>{open_.length ? '⚠' : '✓'}</span>
        {open_.length ? open_.length : null}
        <span className="sr-only">{open_.length ? 'things to check' : 'In good standing'}</span>
      </button>
      {open && (
        <div
          className="absolute left-0 z-30 mt-2 w-[min(calc(100vw-2rem),26rem)] md:left-auto md:right-0 rounded-card border border-neutral-200 bg-white p-4 text-left shadow-lg"
          // An action inside (Open configuration, Set up rate card) moves the page on; close behind it.
          onClick={(e) => { if ((e.target as HTMLElement).closest('a,button')) setOpen(false); }}
        >
          <h2 className="text-caption font-semibold uppercase tracking-wider text-neutral-700">
            {open_.length ? 'Needs attention' : 'In good standing'}
          </h2>
          <Checklist items={shown} className="mt-3" />
        </div>
      )}
    </div>
  );
}

function statusTone(s: TenantStatus): Tone {
  switch (s) {
    case 'ACTIVE': return 'covered';
    case 'PENDING': return 'dueSoon';
    case 'SUSPENDED': return 'overdue';
    default: return 'neutral';
  }
}

/**
 * The four numbers that describe the account. A figure this reader may not
 * see says so plainly rather than showing a zero, which would read as a fact
 * about the company.
 */
function figures(
  c: CompanyShape,
  s: OnboardingStatus | null,
  can: Can,
  b: { outstanding: number | null; billingLoading: boolean; billingError: string | null; setupLoading: boolean; onBilling: () => void },
) {
  const pending = c.status === 'PENDING';
  const orgRows = c.orgVisible !== false;
  const stated = c.workforceCount ?? null;
  const rollFromOrg = orgRows ? c.plants.reduce((n, p) => n + (p.employees ?? 0), 0) : 0;
  const onRoll = s ? s.counts.employees : rollFromOrg;
  const unknown = pending ? 'After approval' : 'Not shown to your role';

  return [
    {
      key: 'workers', label: 'Employees',
      value: onRoll > 0
        ? onRoll.toLocaleString('en-IN')
        : stated != null ? stated.toLocaleString('en-IN') : c.workforceBand ?? '—',
      hint: onRoll > 0
        ? (stated != null && stated !== onRoll ? `On the roll · ${stated.toLocaleString('en-IN')} stated` : 'On the roll')
        : stated != null || c.workforceBand ? 'Stated at registration' : 'Not stated',
      loading: b.setupLoading,
    },
    {
      key: 'plants', label: 'Plants',
      value: s ? s.counts.plants : orgRows ? c.plants.length : '—',
      hint: s ? undefined : orgRows ? undefined : unknown,
      loading: b.setupLoading,
    },
    {
      // Invited users are counted: the administrator made at registration is
      // one until they open the welcome link, and "0" read as nobody at all.
      key: 'logins', label: 'Logins',
      value: s ? s.counts.users + (s.counts.invitedUsers ?? 0) : orgRows ? c.users.length : '—',
      hint: s
        ? (s.counts.invitedUsers ? `${s.counts.invitedUsers} invited, not signed in yet` : 'Company users who can sign in')
        : orgRows ? 'Company users who can sign in' : pending ? 'Created at approval' : unknown,
      loading: b.setupLoading,
    },
    can.billing && !pending && {
      key: 'outstanding', label: 'Outstanding',
      value: b.outstanding == null ? '—' : money(b.outstanding),
      hint: b.billingError ? 'Billing could not be read' : b.outstanding ? 'Unpaid on issued invoices' : 'Nothing owed',
      tone: b.outstanding ? 'overdue' as const : undefined,
      loading: b.billingLoading,
      onClick: b.onBilling,
    },
  ];
}

/**
 * What is right with the account and what is not, in the order somebody
 * would fix it: can they sign in, is the account set up, do they owe us, can
 * we reach them. Each line that is not right says where to go — and only
 * offers a way there to a reader who may take it.
 */
function needs(
  c: CompanyShape,
  s: OnboardingStatus | null,
  outstanding: number | null,
  billingLoading: boolean,
  can: Can,
  go?: { setTab: (k: string) => void; onEdit: () => void },
  rates?: { items: RateCardRow[] } | null,
): CheckItem[] {
  const jump = (label: string, tab: string) => go && (
    <button type="button" onClick={() => go.setTab(tab)} className="inline-flex min-h-6 items-center tap-area text-caption font-medium text-primary-600 hover:text-primary-700">
      {label}
    </button>
  );
  const hasContact = Boolean(c.contactName || c.contactPhone || c.contactEmail);
  const items: CheckItem[] = [];

  if (c.status === 'PENDING') {
    items.push({
      key: 'account', state: 'warning', label: 'Registration awaiting approval',
      detail: `Paperwork: ${paperwork(c.onboardingState).toLowerCase()}. Nobody can sign in until it is approved.`,
    });
  } else if (c.status === 'ACTIVE') {
    items.push({ key: 'account', state: 'done', label: 'Account active' });
  } else if (c.status === 'SUSPENDED') {
    items.push({ key: 'account', state: 'blocked', label: 'Account suspended', detail: 'Everyone at the company is signed out until it is reinstated.' });
  } else {
    items.push({ key: 'account', state: 'blocked', label: 'Account closed' });
  }

  if (c.status === 'ACTIVE' || c.status === 'SUSPENDED') {
    if (!c.welcomeEmailSentAt && c.status === 'ACTIVE') {
      items.push({
        key: 'welcome', state: 'warning', label: 'Welcome email not sent',
        detail: can.lifecycle ? 'Send it from Account, on the Overview tab.' : 'An administrator can send it from this page.',
      });
    }
  }

  // A card in force today with at least one price. The platform's own list
  // card can appear in this read with none of its lines visible, and prices
  // nothing on an invoice, so it does not count.
  if (rates && (c.status === 'ACTIVE' || c.status === 'SUSPENDED')) {
    const today = new Date().toISOString().slice(0, 10);
    const card = rates.items.find((r) => r.status === 'PUBLISHED' && r.lines.length > 0
      && r.effectiveFrom.slice(0, 10) <= today && (!r.effectiveTo || r.effectiveTo.slice(0, 10) >= today));
    items.push(card
      ? { key: 'ratecard', state: 'done', label: `Rate card: ${card.name}` }
      : {
          key: 'ratecard', state: 'warning', label: 'No billing rate card',
          detail: 'Medicals can go ahead, but nothing can be invoiced until a rate card is published for this company.',
          action: go ? (
            <Link to={`/admin/billing?tab=cards&company=${c.id}`} className="inline-flex min-h-6 items-center tap-area text-caption font-medium text-primary-600 hover:text-primary-700">
              Set up rate card
            </Link>
          ) : undefined,
        });
  }

  if (can.billing && c.status !== 'PENDING' && !billingLoading && outstanding != null) {
    items.push(outstanding > 0
      ? { key: 'billing', state: 'warning', label: `${money(outstanding)} outstanding`, detail: 'Unpaid on issued invoices.', action: jump('Open billing', 'billing') }
      : { key: 'billing', state: 'done', label: 'Nothing outstanding' });
  }

  items.push(hasContact
    ? { key: 'contact', state: 'done', label: `Contact: ${[c.contactName, c.contactPhone ?? c.contactEmail].filter(Boolean).join(' · ')}` }
    : {
        key: 'contact', state: 'warning', label: 'No contact recorded', detail: 'Nobody to call about this account.',
        action: go && can.edit ? (
          <button type="button" onClick={go.onEdit} className="inline-flex min-h-6 items-center tap-area text-caption font-medium text-primary-600 hover:text-primary-700">Add one</button>
        ) : undefined,
      });

  return items;
}

/* ── Overview ───────────────────────────────────────────────── */

/**
 * Who to call and where they are first, then the account's own settings,
 * then the registration paperwork — and, set apart at the foot, the one
 * privileged act the page offers.
 */
function Overview({ company: c, can, onEdit, onChanged }: { company: CompanyShape; can: Can; onEdit: () => void; onChanged: () => void }) {
  const edit = can.edit ? (
    <button type="button" onClick={onEdit} className="-mx-1.5 inline-flex min-h-6 items-center tap-area rounded px-1.5 text-caption font-medium text-primary-600 hover:bg-primary-50 hover:text-primary-700">
      Edit
    </button>
  ) : undefined;
  const none = (text: string) => <span className="text-neutral-500">{text}</span>;
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-3">
        <DetailSection title="Primary contact" action={edit}>
          <ContactBlock contact={c}>
            {c.contactDesignation && (
              <div className="flex items-baseline justify-between gap-4 py-1.5">
                <span className="shrink-0 text-caption text-neutral-500">Designation</span>
                <span className="text-right text-body text-neutral-900">{c.contactDesignation}</span>
              </div>
            )}
          </ContactBlock>
        </DetailSection>

        <DetailSection
          title="Registered address"
          phoneFolded
          action={(
            <span className="flex items-center gap-2">
              <InfoTip label="About the registered address">
                Where contracts and invoices are addressed. Each plant keeps its own address.
              </InfoTip>
              {edit}
            </span>
          )}
        >
          <AddressBlock address={c} />
        </DetailSection>

        <DetailSection title="Account">
          <Facts
            columns={2}
            className="lg:grid-cols-1"
            facts={[
              { label: 'Setup', value: c.setupMode ? SETUP_MODE_LABEL[c.setupMode] : none('Not chosen') },
              {
                label: 'Employee IDs',
                value: c.employeeCodePrefix ? <span className="font-mono">{c.employeeCodePrefix}-000001 onwards</span> : 'Typed by HR',
                hint: c.employeeCodePrefix ? `Next ${c.employeeCodePrefix}-${String(c.employeeCodeNextSeq ?? 1).padStart(6, '0')}` : undefined,
              },
              c.status !== 'PENDING' && {
                label: 'Welcome email',
                value: c.welcomeEmailSentAt ? `Sent ${formatDateTime(c.welcomeEmailSentAt)}` : none('Not sent'),
              },
            ]}
          />
          {c.status === 'ACTIVE' && can.lifecycle && <ResendWelcome tenantId={c.id} sent={Boolean(c.welcomeEmailSentAt)} />}
        </DetailSection>
      </div>

      <DetailSection title="Registration details" phoneFolded action={edit}>
        <Facts
          columns={4}
          facts={[
            { label: 'Legal name', value: c.legalName ?? none('Not recorded') },
            {
              label: 'GSTIN',
              // Verified only when this server fetched that exact GSTIN from
              // the GST registry before saving; typed by hand says so. The GST
              // status is the registry's, not this company's status here.
              value: c.gstin ? (
                <span>
                  <span className="font-mono">{c.gstin}</span>
                  <span className="block text-caption text-neutral-500">
                    {c.gstVerifiedAt
                      ? `Checked with GST on ${formatDate(c.gstVerifiedAt)}${c.gstStatus ? ` · GST status: ${c.gstStatus}` : ''}`
                      : 'Entered by hand, not checked with GST'}
                  </span>
                </span>
              ) : none('Not recorded'),
            },
            { label: 'Industry', value: c.industryRef?.name ?? c.industry ?? none('Not chosen') },
            { label: 'Registered on', value: formatDate(c.createdAt) },
            {
              label: 'Workforce stated',
              value: [c.workforceBand, c.workforceCount != null ? `${c.workforceCount.toLocaleString('en-IN')} people` : null].filter(Boolean).join(' · ') || none('Not stated'),
            },
            { label: 'Paperwork', value: paperwork(c.onboardingState) },
            c.website ? {
              label: 'Website',
              value: <a href={c.website} target="_blank" rel="noreferrer" className="break-all text-primary-700 hover:underline">{c.website}</a>,
            } : null,
          ]}
        />
      </DetailSection>

      {/* The profile extras and the signed paperwork (legacy-app parity P2). Kept
          by platform staff; HR sees the same facts read-only on its
          settings page. */}
      <CompanyExtrasSection company={c} canEdit={can.edit} onSaved={onChanged} />
      <AgreementsSection company={c} canEdit={can.edit} />
    </div>
  );
}

/** The paperwork state in words; a rejection carries its reason after the colon. */
function paperwork(state: string): string {
  if (ONBOARDING_LABEL[state]) return ONBOARDING_LABEL[state]!;
  if (state.startsWith('REJECTED:')) return `Rejected — ${state.slice(9).trim()}`;
  const s = state.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function EditDialog({ company, onClose, onSaved }: {
  company: CompanyShape; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState<Record<string, string>>(() => ({
    name: company.name,
    legalName: company.legalName ?? '',
    gstin: company.gstin ?? '',
    contactName: company.contactName ?? '',
    contactDesignation: company.contactDesignation ?? '',
    contactPhone: company.contactPhone ?? '',
    contactAltPhone: company.contactAltPhone ?? '',
    contactEmail: company.contactEmail ?? '',
    website: company.website ?? '',
    employeeCodePrefix: company.employeeCodePrefix ?? '',
    workforceBand: company.workforceBand ?? '',
    workforceCount: company.workforceCount != null ? String(company.workforceCount) : '',
    setupMode: company.setupMode ?? 'SELF_SERVICE',
  }));
  const [industry, setIndustry] = useState<Industry | null>(industryFromRef(company.industryRef));
  const [address, setAddress] = useState<AddressFormValue>(addressForm(company));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const set = (k: string) => (v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((e) => { const { [k]: _, ...rest } = e; return rest; });
  };

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (form.name.trim().length < 2) e.name = 'Company name is required';
    if (form.gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z][Z][0-9A-Z]$/.test(form.gstin)) e.gstin = 'Enter a valid GSTIN';
    if (form.contactPhone && !/^[6-9]\d{9}$/.test(form.contactPhone)) e.contactPhone = 'Enter a valid mobile number';
    if (form.contactAltPhone && !/^[6-9]\d{9}$/.test(form.contactAltPhone)) e.contactAltPhone = 'Enter a valid mobile number';
    if (form.contactEmail && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contactEmail)) e.contactEmail = 'Enter a valid email address';
    if (form.website && !/^https?:\/\//.test(form.website)) e.website = 'Start with https://';
    if (form.employeeCodePrefix && !/^[A-Z0-9]{2,8}$/.test(form.employeeCodePrefix)) e.employeeCodePrefix = 'Use 2 to 8 capital letters or digits';
    // The street is optional, as at registration: the pincode is what the
    // plant and the primary location are made from.
    if (address.pincode && address.pincode.length !== 6) e.pincode = 'Enter a valid 6-digit Indian pincode';
    return e;
  }

  async function save() {
    const local = validate();
    setFieldErrors(local);
    if (Object.keys(local).length) { setError('Please correct the highlighted fields.'); return; }
    setBusy(true); setError(null);
    try {
      const nullable = (k: string) => (form[k]?.trim() ? form[k].trim() : null);
      await api.patch(`/tenants/${company.id}`, {
        name: form.name.trim(),
        legalName: nullable('legalName'),
        gstin: form.gstin ? form.gstin.toUpperCase() : null,
        industryId: industry?.id ?? null,
        contactName: nullable('contactName'),
        contactDesignation: nullable('contactDesignation'),
        contactPhone: nullable('contactPhone'),
        contactAltPhone: nullable('contactAltPhone'),
        contactEmail: nullable('contactEmail'),
        website: nullable('website'),
        employeeCodePrefix: nullable('employeeCodePrefix'),
        workforceBand: form.workforceBand || null,
        workforceCount: form.workforceCount ? Number(form.workforceCount) : null,
        setupMode: form.setupMode,
        ...addressPayload(address, { clearable: true }),
      });
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.hasFieldErrors) setFieldErrors(e.fieldErrors);
      setError(errorMessage(e));
    } finally { setBusy(false); }
  }

  return (
    <Dialog title={`Edit ${company.name}`} onClose={onClose} wide>
      <div className="mt-2">
        <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">Company</h3>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <TextField label="Trading name" value={form.name} required error={fieldErrors.name} onChange={set('name')} />
          <TextField label="Legal name" value={form.legalName} error={fieldErrors.legalName} onChange={set('legalName')} />
          <TextField label="GSTIN" value={form.gstin} mono error={fieldErrors.gstin}
            onChange={(v) => set('gstin')(v.toUpperCase())} />
          <IndustryCombobox value={industry} onChange={(i) => { setIndustry(i); setFieldErrors((e) => { const { industryId: _, ...r } = e; return r; }); }} error={fieldErrors.industryId} />
          <TextField label="Employee ID prefix" value={form.employeeCodePrefix} mono error={fieldErrors.employeeCodePrefix}
            onChange={(v) => set('employeeCodePrefix')(v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
            status={form.employeeCodePrefix ? `Generated IDs: ${form.employeeCodePrefix}-000001…` : 'Optional. Lets HR add people without typing an ID.'} />
          <SelectField label="Setup mode" value={form.setupMode} onChange={set('setupMode')}>
            <option value="SELF_SERVICE">{SETUP_MODE_LABEL.SELF_SERVICE}</option>
            <option value="SUPPORT_ASSISTED">{SETUP_MODE_LABEL.SUPPORT_ASSISTED}</option>
          </SelectField>
          <SelectField label="Workforce" value={form.workforceBand} onChange={set('workforceBand')} error={fieldErrors.workforceBand}>
            <option value="">Not known</option>
            {WORKFORCE_BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
          </SelectField>
          <TextField label="Exact headcount" value={form.workforceCount} inputMode="numeric" error={fieldErrors.workforceCount}
            onChange={(v) => set('workforceCount')(v.replace(/\D/g, ''))} />
        </div>
      </div>

      <div className="mt-4">
        <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">
          Registered address
        </h3>
        <div className="mt-2"><AddressFields value={address} onChange={setAddress} errors={fieldErrors} /></div>
      </div>

      <div className="mt-4">
        <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">Who to call</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField label="Contact person" value={form.contactName} error={fieldErrors.contactName} onChange={set('contactName')} />
          <TextField label="Designation" value={form.contactDesignation} error={fieldErrors.contactDesignation} onChange={set('contactDesignation')} />
          <TextField label="Mobile" value={form.contactPhone} mono error={fieldErrors.contactPhone}
            onChange={(v) => set('contactPhone')(v.replace(/\D/g, '').slice(0, 10))} />
          <TextField label="Alternate phone" value={form.contactAltPhone} mono error={fieldErrors.contactAltPhone}
            onChange={(v) => set('contactAltPhone')(v.replace(/\D/g, '').slice(0, 10))} />
          <TextField label="Email" value={form.contactEmail} type="email" error={fieldErrors.contactEmail} onChange={set('contactEmail')} />
          <TextField label="Website" value={form.website} error={fieldErrors.website} onChange={set('website')} placeholder="https://" />
        </div>
      </div>

      {error && <Alert className="mt-3">{error}</Alert>}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={save} loading={busy}>Save changes</Button>
      </div>
    </Dialog>
  );
}

/** "Send it again" — after a bounce, or when the customer never saw it. */
function ResendWelcome({ tenantId, sent }: { tenantId: string; sent: boolean }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  async function resend() {
    setBusy(true); setNote(null);
    try {
      const r = await api.post<{ queued: number; suppressed: string[] }>(`/tenants/${tenantId}/welcome-email`, {});
      setNote(r.queued > 0 ? `Queued to ${r.queued} address${r.queued === 1 ? '' : 'es'}.` : `Nothing sent: ${r.suppressed.join('; ') || 'no administrator with an email'}.`);
    } catch (e) { setNote(errorMessage(e)); } finally { setBusy(false); }
  }
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3">
      <Button size="sm" variant="secondary" onClick={resend} loading={busy}>
        {sent ? 'Resend welcome email' : 'Send welcome email'}
      </Button>
      {note && <span role="status" className="text-caption text-neutral-600">{note}</span>}
    </div>
  );
}

/**
 * Approve, reject, suspend, reinstate.
 *
 * On the page rather than in a modal of its own, because whoever is deciding
 * has just read the registration on the tabs above it. Approval mints the
 * first password once and never again — the panel that shows it is the only
 * place it exists.
 */
const DANGER_OUTLINE = 'border-danger-600/40 text-danger-600 hover:bg-danger-100';

function Lifecycle({ company: c, approveAsk = 0, onChanged }: { company: CompanyShape; approveAsk?: number; onChanged: () => void }) {
  const [mode, setMode] = useState<null | 'approve' | 'reject' | 'suspend' | 'reinstate'>(null);
  useEffect(() => { if (approveAsk && c.status === 'PENDING') setMode('approve'); }, [approveAsk, c.status]);
  // The registration's contact is who approval would make administrator, so
  // the dialog starts from them rather than asking for both again.
  const [form, setForm] = useState({
    adminEmail: c.contactEmail ?? '', adminFullName: c.contactName ?? '', note: '', reason: '',
  });
  const [sendWelcome, setSendWelcome] = useState(true);
  const [issued, setIssued] = useState<{ email: string; invitation: { expiresAt: string } | { failed: string }; welcome: { queued: number; suppressed: string[] } | null } | null>(null);
  const idem = useIdempotencyKey();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => { setMode(null); setError(null); };

  async function approve() {
    setBusy(true); setError(null);
    try {
      const res = await api.post<{
        administrator: { id: string; email: string };
        welcomeEmail?: { queued: number; suppressed: string[] };
        invitation?: { expiresAt: string } | { failed: string } | null;
      }>(
        `/tenants/${c.id}/approve`,
        {
          adminEmail: form.adminEmail.trim().toLowerCase(),
          adminFullName: form.adminFullName,
          ...(form.note ? { note: form.note } : {}),
          sendWelcomeEmail: sendWelcome,
        },
        idem.key(),
      );
      idem.rotate();
      // The administrator sets their own password from an emailed link;
      // nobody here ever sees it. The company is active whether or not that
      // email goes out, so a delivery failure is reported, not fatal — the
      // setup tab offers a resend.
      // The welcome email carries the sign-in link now (one email, not two);
      // only when it was switched off is the bare invitation sent here.
      let invitation: { expiresAt: string } | { failed: string };
      if (res.invitation) invitation = res.invitation;
      else {
        try {
          const inv = await api.post<{ expiresAt: string }>(`/admin/tenants/${c.id}/setup/users/${res.administrator.id}/invite`);
          invitation = { expiresAt: inv.expiresAt };
        } catch (e) { invitation = { failed: errorMessage(e) }; }
      }
      setIssued({ email: res.administrator.email, invitation, welcome: res.welcomeEmail ?? null });
      onChanged();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  async function reject() {
    setBusy(true); setError(null);
    try {
      await api.post(`/tenants/${c.id}/reject`, { reason: form.reason });
      onChanged(); close();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  async function setStatus(status: 'ACTIVE' | 'SUSPENDED') {
    setBusy(true); setError(null);
    try {
      await api.post(`/tenants/${c.id}/status`, {
        status,
        reason: form.reason || (status === 'SUSPENDED' ? 'Suspended by the platform' : 'Reinstated'),
      });
      onChanged(); close();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <>
      {/* The destructive acts are outlined in the danger colour rather than
          filled: on a page that is read far more often than it is acted on,
          "Suspend" should not be the loudest thing on it. Their confirmations
          carry the filled danger button. */}
      {c.status === 'PENDING' && (
        <>
          <Button variant="secondary" className={DANGER_OUTLINE} onClick={() => setMode('reject')}>Reject</Button>
          <Button onClick={() => setMode('approve')}>Approve</Button>
        </>
      )}
      {c.status === 'ACTIVE' && (
        <Button variant="secondary" className={DANGER_OUTLINE} onClick={() => setMode('suspend')}>Suspend</Button>
      )}
      {c.status === 'SUSPENDED' && (
        <Button onClick={() => setMode('reinstate')}>Reinstate</Button>
      )}

      {issued && (
        <Dialog title="Company activated" onClose={() => { setIssued(null); close(); }}>
          <p className="mt-2 text-sm text-neutral-700">
            {c.name} is active. Their administrator chooses their own password from a link
            sent to their email — nobody on the platform ever holds it.
          </p>
          <div className="mt-3 space-y-2 rounded border border-neutral-200 bg-neutral-50 p-3">
            <div>
              <p className="text-xs text-neutral-500">Administrator</p>
              <p className="font-mono text-sm text-neutral-900">{issued.email}</p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">Invitation</p>
              {'expiresAt' in issued.invitation ? (
                <p className="text-sm text-neutral-900">
                  Sent. The link works once, until {new Date(issued.invitation.expiresAt).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}.
                </p>
              ) : (
                <p className="text-sm text-overdue-600">
                  Not sent: {issued.invitation.failed} Resend it from the Setup tab once that is fixed.
                </p>
              )}
            </div>
          </div>
          <p className="mt-3 text-xs text-neutral-600">
            {issued.welcome && issued.welcome.queued > 0
              ? `A welcome email is on its way to ${issued.welcome.queued === 1 ? 'the administrator' : `${issued.welcome.queued} addresses`}. It names the company and the sign-in address; it never carries a password.`
              : issued.welcome?.suppressed.length
                ? `No welcome email was sent: ${issued.welcome.suppressed.join('; ')}.`
                : 'No welcome email was sent.'}
          </p>
          <div className="mt-5 flex justify-end">
            <Button onClick={() => { setIssued(null); close(); }}>Done</Button>
          </div>
        </Dialog>
      )}

      {mode === 'approve' && !issued && (
        <Dialog title={`Approve ${c.name}`} onClose={close}>
          <p className="mt-1 text-sm text-neutral-600">
            Approving activates the company and creates its first administrator,
            in one act. They get an HR Admin role over the whole company.
          </p>
          <TextField label="Administrator name" value={form.adminFullName} autoFocus required
            onChange={(v) => setForm({ ...form, adminFullName: v })} />
          <TextField label="Administrator email" value={form.adminEmail} mono required type="email"
            onChange={(v) => setForm({ ...form, adminEmail: v })}
            hint="They get a one-time link to choose their own password." />
          <TextField label="Note" value={form.note}
            onChange={(v) => setForm({ ...form, note: v })}
            hint="Recorded on the approval." />
          <label className="mt-3 flex items-start gap-2 text-sm text-neutral-700">
            <Input type="checkbox" checked={sendWelcome} onChange={(e) => setSendWelcome(e.target.checked)} className="mt-0.5" />
            <span>
              Send the welcome email
              <span className="block text-xs text-neutral-500">
                To the administrator{c.contactEmail ? ` and ${c.contactEmail}` : ''}. Says the company is approved and how to get started — no password.
              </span>
            </span>
          </label>
          {error && <Alert>{error}</Alert>}
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="secondary" onClick={close} disabled={busy}>Cancel</Button>
            <Button
              onClick={approve}
              loading={busy}
              disabled={!form.adminEmail.includes('@') || form.adminFullName.trim().length < 2}
            >
              Approve and activate
            </Button>
          </div>
        </Dialog>
      )}

      {mode === 'reject' && (
        <Dialog title={`Reject ${c.name}`} onClose={close}>
          <p className="mt-1 text-sm text-neutral-600">
            The registration is closed and nobody at {c.name} is given access. It cannot be
            approved afterwards — they would register again.
          </p>
          <TextField label="Reason" value={form.reason} autoFocus required
            onChange={(v) => setForm({ ...form, reason: v })}
            hint="At least 10 characters."
            info="Stored, and the next conversation with this company starts from it." />
          {error && <Alert>{error}</Alert>}
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="secondary" onClick={close} disabled={busy}>Cancel</Button>
            <Button variant="danger" onClick={reject} loading={busy}
              disabled={form.reason.trim().length < 10}>
              Reject the registration
            </Button>
          </div>
        </Dialog>
      )}

      {(mode === 'suspend' || mode === 'reinstate') && (
        <Dialog
          title={mode === 'suspend' ? `Suspend ${c.name}` : `Reinstate ${c.name}`}
          onClose={close}
        >
          <p className="mt-1 text-sm text-neutral-600">
            {mode === 'suspend'
              ? 'Everyone at this company is signed out and cannot sign back in. Clinical records and certificates already signed are untouched.'
              : 'Their people can sign in again from the moment this is saved.'}
          </p>
          <TextField label="Reason" value={form.reason} autoFocus required
            onChange={(v) => setForm({ ...form, reason: v })}
            hint="At least 5 characters. Recorded on the change." />
          {error && <Alert>{error}</Alert>}
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="secondary" onClick={close} disabled={busy}>Cancel</Button>
            <Button
              variant={mode === 'suspend' ? 'danger' : 'primary'}
              onClick={() => setStatus(mode === 'suspend' ? 'SUSPENDED' : 'ACTIVE')}
              loading={busy}
              disabled={form.reason.trim().length < 5}
            >
              {mode === 'suspend' ? 'Suspend' : 'Reinstate'}
            </Button>
          </div>
        </Dialog>
      )}
    </>
  );
}

